/**
 * Pure transformation functions for canvas device-mode changes.
 *
 * Extracted so they can be unit-tested independently of ScreenBuilder state.
 */

/**
 * Transform a single element's position/size for the given scaling mode.
 * Also transforms any subElements (free-position mode) so their positions
 * stay consistent with the parent canvas change.
 *
 * @param {object} element
 * @param {{ width: number, height: number }} oldDims
 * @param {{ width: number, height: number }} newDims
 * @param {'proportional'|'center'|'none'} scalingOption
 * @returns {object} transformed element (new reference)
 */
export function transformElement(element, oldDims, newDims, scalingOption) {
  // Locked elements are immune to automated layout changes.
  if (element.isLocked) return { ...element };

  if (scalingOption === 'proportional') {
    const scaleX = newDims.width / oldDims.width;
    const scaleY = newDims.height / oldDims.height;

    const transformedSubs = element.subElements?.map(sub => ({
      ...sub,
      position: {
        x: Math.round(sub.position.x * scaleX),
        y: Math.round(sub.position.y * scaleY),
      },
      size: {
        width: Math.round(sub.size.width * scaleX),
        height: Math.round(sub.size.height * scaleY),
      },
    }));

    return {
      ...element,
      position: {
        x: Math.round(element.position.x * scaleX),
        y: Math.round(element.position.y * scaleY),
      },
      size: {
        width: Math.round(element.size.width * scaleX),
        height: Math.round(element.size.height * scaleY),
      },
      ...(transformedSubs ? { subElements: transformedSubs } : {}),
    };
  }

  if (scalingOption === 'center') {
    const offsetX = (newDims.width - oldDims.width) / 2;
    const offsetY = (newDims.height - oldDims.height) / 2;

    const clamp = (val, lo, hi) => Math.max(lo, Math.min(val, hi));

    const transformedSubs = element.subElements?.map(sub => ({
      ...sub,
      position: {
        x: clamp(sub.position.x + offsetX, 0, newDims.width - sub.size.width),
        y: clamp(sub.position.y + offsetY, 0, newDims.height - sub.size.height),
      },
    }));

    return {
      ...element,
      position: {
        x: clamp(element.position.x + offsetX, 0, newDims.width - element.size.width),
        y: clamp(element.position.y + offsetY, 0, newDims.height - element.size.height),
      },
      ...(transformedSubs ? { subElements: transformedSubs } : {}),
    };
  }

  // 'none' — return unchanged (still a new reference for immutability)
  return { ...element };
}

/**
 * Transform all elements across all screens for a device mode change.
 *
 * @param {Array} screens
 * @param {{ width: number, height: number }} oldDims
 * @param {{ width: number, height: number }} newDims
 * @param {string} newMode  device mode key
 * @param {'proportional'|'center'|'none'} scalingOption
 * @returns {Array} updated screens array (new references)
 */
export function transformScreensForDeviceChange(screens, oldDims, newDims, newMode, scalingOption) {
  return screens.map(screen => ({
    ...screen,
    layout: {
      ...screen.layout,
      width: newDims.width,
      height: newDims.height,
      deviceMode: newMode,
    },
    elements: screen.elements
      ? screen.elements.map(el => transformElement(el, oldDims, newDims, scalingOption))
      : screen.elements,
  }));
}
